import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Table, Tooltip } from 'antd'
import { EntryPropertiesPopup } from 'features/drilldown/components/ontology/EntryPropertiesView'
import { PropertyValueEditor } from 'features/drilldown/components/ontology/PropertyValueEditor'
import { PropertyValueRenderer } from 'features/drilldown/components/ontology/PropertyValueRenderer'
import { OntologyFieldType } from 'features/dynamicOntology'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const LoadingContainer = styled.div`
  text-align: center;
  padding: 2rem;
`

const TableContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const LoadingIcon = styled(LoadingOutlined)`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.gecko_brand['400']};
`

const CurrentValueCell = styled.div<{
  $hasDifference?: boolean
}>`
  background-color: ${({ $hasDifference }) =>
    $hasDifference ? 'rgba(255, 0, 0, 0.1)' : 'transparent'};
  padding: 8px;
  border-radius: 4px;
`

const NewValueCell = styled.div<{
  $isEdited?: boolean
  $hasDifference?: boolean
}>`
  padding: 8px;
  border-radius: 4px;
  color: ${({ $hasDifference }) =>
    !$hasDifference ? CantileverTheme.colors.gray['600'] : 'inherit'};
  display: flex;
  align-items: center;
  gap: 8px;
  border: ${({ $isEdited, $hasDifference }) => {
    if ($isEdited) {
      return `2px solid ${CantileverTheme.colors.success['400_base']}`
    }
    if ($hasDifference) {
      return `2px solid ${CantileverTheme.colors.blue['400']}`
    }
    return 'none'
  }};
  background-color: ${({ $isEdited, $hasDifference }) => {
    if ($isEdited) {
      return `${CantileverTheme.colors.success['400_base']}10`
    }
    if ($hasDifference) {
      return `${CantileverTheme.colors.blue['400']}10`
    }
    return 'transparent'
  }};
`

const TickIcon = styled(CheckCircleOutlined)<{
  $isEdited?: boolean
}>`
  color: ${({ $isEdited }) =>
    $isEdited
      ? CantileverTheme.colors.success['400_base']
      : CantileverTheme.colors.blue['400']};
  font-size: 14px;
  flex-shrink: 0;
`

const CrossIcon = styled(CloseCircleOutlined)`
  color: ${CantileverTheme.colors.gray['600']};
  font-size: 14px;
  flex-shrink: 0;
`

export interface TableRowData {
  key: string
  id: string
  currentValue: unknown
  newValue?: unknown
  isEdited?: boolean
  hasDifference?: boolean
  entryData?: Record<string, unknown>
}

interface BulkEditPreviewTableProps {
  data: ReadonlyArray<TableRowData>
  isLoading: boolean
  onValueChange?: (id: string, newValue: unknown) => void
  isEditable?: boolean
  fieldType?: OntologyFieldType
  fieldKey?: string
  onValidationChange?: (hasErrors: boolean) => void
}

interface CurrentValueProps {
  value: unknown
  record: TableRowData
  fieldType?: OntologyFieldType
}

interface NewValueProps {
  value: unknown
  record: TableRowData
  onValueChange?: (id: string, newValue: unknown) => void
  isEditable?: boolean
  fieldType?: OntologyFieldType
  fieldKey?: string
  onRowValidationChange?: (id: string, isValid: boolean) => void
}

const CurrentValue: React.FC<CurrentValueProps> = ({
  value,
  record,
  fieldType,
}: CurrentValueProps) => (
  <CurrentValueCell $hasDifference={record.hasDifference}>
    <PropertyValueRenderer value={value} type={fieldType || 'text'} />
  </CurrentValueCell>
)

const NewValue: React.FC<NewValueProps> = ({
  value,
  record,
  onValueChange,
  isEditable,
  fieldType,
  fieldKey,
  onRowValidationChange,
}: NewValueProps) => {
  // We show a tick only when there's a difference and the new value is not empty
  const showTick =
    record.hasDifference &&
    record.newValue !== record.currentValue &&
    record.newValue !== null &&
    record.newValue !== undefined &&
    record.newValue !== ''

  // We show a cross when there's no difference (no change will be made)
  const showCross = !record.hasDifference

  if (isEditable && onValueChange && fieldType) {
    return (
      <NewValueCell
        $isEdited={record.isEdited}
        $hasDifference={record.hasDifference}>
        <PropertyValueEditor
          type={fieldType}
          value={value}
          onChange={(newValue) => onValueChange(record.id, newValue)}
          fieldKey={fieldKey}
          size='small'
          rows={1}
          onValidationChange={(isValid) =>
            onRowValidationChange?.(record.id, isValid)
          }
        />
        {showTick && (
          <Tooltip
            title={
              record.isEdited ? 'User-edited value' : 'Global prefilled value'
            }>
            <TickIcon $isEdited={record.isEdited} />
          </Tooltip>
        )}
        {showCross && (
          <Tooltip title='No change will be made to this entry'>
            <CrossIcon />
          </Tooltip>
        )}
      </NewValueCell>
    )
  }

  return (
    <NewValueCell
      $isEdited={record.isEdited}
      $hasDifference={record.hasDifference}>
      <PropertyValueRenderer value={value} type={fieldType || 'text'} />
      {showTick && (
        <Tooltip
          title={
            record.isEdited ? 'User-edited value' : 'Global prefilled value'
          }>
          <TickIcon $isEdited={record.isEdited} />
        </Tooltip>
      )}
      {showCross && (
        <Tooltip title='No change will be made to this entry'>
          <CrossIcon />
        </Tooltip>
      )}
    </NewValueCell>
  )
}

const LoadingSpinner: FC<{ message: string }> = ({ message }) => (
  <LoadingContainer>
    <LoadingIcon />
    <div style={{ marginTop: '1rem' }}>{message}</div>
  </LoadingContainer>
)

export const BulkEditPreviewTable: FC<BulkEditPreviewTableProps> = ({
  data,
  isLoading,
  onValueChange,
  isEditable = false,
  fieldType,
  fieldKey,
}) => {
  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 200,
        color: CantileverTheme.colors.error['200'],
        ellipsis: false,
        render: (id: string, record: TableRowData) =>
          record.entryData ? (
            <EntryPropertiesPopup entryData={record.entryData}>
              {id}
            </EntryPropertiesPopup>
          ) : (
            <span>{id}</span>
          ),
      },
      {
        title: 'Current Value',
        dataIndex: 'currentValue',
        key: 'currentValue',
        ellipsis: false,
        width: 200,
        render: (value: unknown, record: TableRowData) => (
          <CurrentValue value={value} record={record} fieldType={fieldType} />
        ),
      },
      {
        title: 'New Value',
        dataIndex: 'newValue',
        key: 'newValue',
        ellipsis: false,
        render: (value: unknown, record: TableRowData) => (
          <NewValue
            value={value}
            record={record}
            onValueChange={onValueChange}
            isEditable={isEditable}
            fieldType={fieldType}
            fieldKey={fieldKey}
          />
        ),
      },
    ],
    [onValueChange, isEditable, fieldType, fieldKey]
  )

  if (isLoading) {
    return <LoadingSpinner message='Loading preview...' />
  }

  return (
    <TableContainer>
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        size='small'
        scroll={{ y: 'calc(100vh - 400px)' }}
        style={{
          marginBottom: '1rem',
          flex: 1,
          height: '100%',
        }}
      />
    </TableContainer>
  )
}
